import React, { useState } from 'react';
import { Container, Form, Button, Alert, Row, Col, ProgressBar, Modal, Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faTimesCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import api from '../api'; // Importa l'istanza axios configurata
import { useMediaQuery } from 'react-responsive';

const FilmImport = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'danger'
    const [csvColumns, setCsvColumns] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showReport, setShowReport] = useState(false);
    const [importReport, setImportReport] = useState(null);
    const [updateExisting, setUpdateExisting] = useState(false);
    
    const isMobile = useMediaQuery({ maxWidth: 991 });

    const filmSchemaFields = [
        { key: 'titolo', label: 'Titolo', required: true },
        { key: 'immagine_url', label: 'URL Immagine' },
        { key: 'anno', label: 'Anno' },
        { key: 'durata', label: 'Durata' },
        { key: 'genere', label: 'Genere' },
        { key: 'regista', label: 'Regista' },
        { key: 'attori', label: 'Attori' },
        { key: 'trama', label: 'Trama' },
        { key: 'casa_di_produzione', label: 'Casa di Produzione' },
        { key: 'casa_di_distribuzione', label: 'Casa di Distribuzione' },
        { key: 'categoria', label: 'Categoria' },
        { key: 'supporto', label: 'Supporto' },
        { key: 'sku', label: 'SKU', keyField: true },
        { key: 'barcode', label: 'Barcode', keyField: true },
        { key: 'barcode_ibs', label: 'Barcode IBS', keyField: true },
        { key: 'prezzo_negozio_base', label: 'Prezzo Base' },
        { key: 'prezzo_negozio', label: 'Prezzo Negozio' },
        { key: 'prezzo_ibs', label: 'Prezzo IBS' },
        { key: 'condizione', label: 'Condizione' },
        { key: 'quantita', label: 'Quantità' },
        { key: 'versione_noleggio', label: 'Versione Noleggio' },
        { key: 'riedizione', label: 'Riedizione' },
        { key: 'prenotabile', label: 'Prenotabile' },
        { key: 'data_uscita', label: 'Data Uscita' },
        { key: 'versione_editoriale', label: 'Versione Editoriale' },
        { key: 'fuori_catalogo', label: 'Fuori Catalogo' },
        { key: 'da_pubblicare_su_ibs', label: 'Pubblicare su IBS' },
        { key: 'in_vetrina', label: 'In Vetrina' },
        { key: 'iva', label: 'IVA' },
        { key: 'costo_acquisto', label: 'Costo Acquisto' },
        { key: 'prezzo_dvdstore', label: 'Prezzo DVD Store' },
        { key: 'tipo_uscita', label: 'Tipo Uscita' },
        { key: 'cg_cod_art', label: 'Cod. Art. CG' },
        { key: 'descrizione_web', label: 'Descrizione Web' },
        { key: 'ubicazione', label: 'Ubicazione' },
        { key: 'fornitore_id', label: 'ID Fornitore' },
        { key: 'tmdb_id', label: 'ID TMDB' },
        { key: 'rating', label: 'Rating' },
        { key: 'note_ibs', label: 'Note IBS' },
    ];

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        setMessage('');
        setCsvColumns([]);
        setColumnMapping({});

        if (file) {
            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await api.post('/film/import/columns', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                const columns = response.data.columns; // Correctly extract the array
                setCsvColumns(columns);

                // Attempt to pre-fill mapping based on exact matches
                const initialMapping = {};
                filmSchemaFields.forEach(field => {
                    const matchedColumn = columns.find(col => col.toLowerCase() === field.label.toLowerCase() || col.toLowerCase() === field.key.toLowerCase());
                    if (matchedColumn) {
                        initialMapping[field.key] = matchedColumn;
                    }
                });
                setColumnMapping(initialMapping);

            } catch (error) {
                console.error('Errore durante il recupero delle colonne:', error);
                setMessage(error.response?.data?.detail || 'Errore durante il recupero delle colonne del file.');
                setMessageType('danger');
            }
        }
    };

    const handleMappingChange = (schemaField, csvColumn) => {
        setColumnMapping(prev => ({
            ...prev,
            [schemaField]: csvColumn === '' ? null : csvColumn,
        }));
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setMessage('Seleziona un file CSV/Excel da importare.');
            setMessageType('danger');
            return;
        }

        // Validazione campi obbligatori
        const hasKeyField = filmSchemaFields
            .filter(field => field.keyField)
            .some(field => columnMapping[field.key]);
        
        if (!hasKeyField) {
            setMessage('Devi mappare almeno un campo chiave (SKU, Barcode o Barcode IBS) per identificare i film.');
            setMessageType('danger');
            return;
        }

        // Se NON stai aggiornando film esistenti, il Titolo è obbligatorio per crearne di nuovi
        if (!updateExisting) {
            const requiredFieldsMapped = filmSchemaFields
                .filter(field => field.required)
                .every(field => columnMapping[field.key]);
            
            if (!requiredFieldsMapped) {
                setMessage('Per creare nuovi film, devi mappare tutti i campi obbligatori (es. Titolo).');
                setMessageType('danger');
                return;
            }
        }

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('mapping', JSON.stringify(columnMapping));
        formData.append('update_existing', updateExisting.toString());

        setUploading(true);
        setUploadProgress(0);
        setMessage('');

        try {
            const response = await api.post('/film/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 180000, // 180 secondi (3 minuti) - stesso timeout del backend
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                },
            });
            
            // Mostra report dettagliato
            setImportReport(response.data);
            setShowReport(true);
            setUploading(false);
            setUploadProgress(0);
            
            // Reset form dopo successo
            setSelectedFile(null);
            setCsvColumns([]);
            setColumnMapping({});
        } catch (error) {
            console.error('Errore durante l\'importazione del file:', error);
            setMessage(error.response?.data?.detail || 'Errore durante l\'importazione del file.');
            setMessageType('danger');
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleCloseReport = () => {
        setShowReport(false);
        setImportReport(null);
    };

    const renderReportModal = () => {
        if (!importReport) return null;

        const hasErrors = importReport.errors > 0;
        const hasSuccess = (importReport.created > 0) || (importReport.updated > 0);
        const allSkipped = importReport.created === 0 && importReport.updated === 0 && importReport.total_rows > 0;

        return (
            <Modal show={showReport} onHide={handleCloseReport} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {hasErrors && !hasSuccess && (
                            <>
                                <FontAwesomeIcon icon={faTimesCircle} className="text-danger me-2" />
                                Importazione con Errori
                            </>
                        )}
                        {hasSuccess && !hasErrors && (
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                                Importazione Completata
                            </>
                        )}
                        {hasSuccess && hasErrors && (
                            <>
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
                                Importazione Parziale
                            </>
                        )}
                        {allSkipped && (
                            <>
                                <FontAwesomeIcon icon={faInfoCircle} className="text-info me-2" />
                                Nessun Film Importato
                            </>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h5 className="mb-3">Riepilogo Importazione</h5>
                    
                    <Table bordered size="sm">
                        <tbody>
                            <tr>
                                <td><strong>Righe totali nel file</strong></td>
                                <td className="text-end">
                                    <Badge bg="secondary">{importReport.total_rows}</Badge>
                                </td>
                            </tr>
                            <tr className="table-success">
                                <td>
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                    <strong>Film creati con successo</strong>
                                </td>
                                <td className="text-end">
                                    <Badge bg="success">{importReport.created}</Badge>
                                </td>
                            </tr>
                            {importReport.updated > 0 && (
                                <tr className="table-info">
                                    <td>
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                        <strong>Film aggiornati</strong>
                                    </td>
                                    <td className="text-end">
                                        <Badge bg="info">{importReport.updated}</Badge>
                                    </td>
                                </tr>
                            )}
                            {importReport.skipped > 0 && (
                                <>
                                    <tr className="table-warning">
                                        <td>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                            <strong>Righe saltate (totale)</strong>
                                        </td>
                                        <td className="text-end">
                                            <Badge bg="warning">{importReport.skipped}</Badge>
                                        </td>
                                    </tr>
                                    {importReport.skipped_existing > 0 && (
                                        <tr>
                                            <td className="ps-4">
                                                <small>SKU già esistente nel database</small>
                                            </td>
                                            <td className="text-end">
                                                <Badge bg="secondary">{importReport.skipped_existing}</Badge>
                                            </td>
                                        </tr>
                                    )}
                                    {importReport.skipped_no_sku > 0 && (
                                        <tr>
                                            <td className="ps-4">
                                                <small>SKU mancante nella riga</small>
                                            </td>
                                            <td className="text-end">
                                                <Badge bg="secondary">{importReport.skipped_no_sku}</Badge>
                                            </td>
                                        </tr>
                                    )}
                                    {importReport.skipped_no_title > 0 && (
                                        <tr>
                                            <td className="ps-4">
                                                <small>Titolo mancante (obbligatorio)</small>
                                            </td>
                                            <td className="text-end">
                                                <Badge bg="secondary">{importReport.skipped_no_title}</Badge>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                            {importReport.errors > 0 && (
                                <tr className="table-danger">
                                    <td>
                                        <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                        <strong>Errori durante l'importazione</strong>
                                    </td>
                                    <td className="text-end">
                                        <Badge bg="danger">{importReport.errors}</Badge>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {importReport.skipped_existing_skus && importReport.skipped_existing_skus.length > 0 && (
                        <div className="mt-3">
                            <h6 className="text-warning">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                Report SKU già presenti nel database ({importReport.skipped_existing_skus.length})
                            </h6>
                            <Alert variant="warning" className="mb-0 p-2">
                                <p className="mb-2 small">
                                    I seguenti SKU erano già presenti e sono stati <strong>saltati</strong>. 
                                    Puoi importare di nuovo il file con l'opzione <em>"Aggiorna film esistenti"</em> per sovrascrivere i dati.
                                </p>
                                <Table bordered size="sm" className="mb-0 bg-white">
                                    <thead className="table-secondary">
                                        <tr>
                                            <th>#</th>
                                            <th>SKU</th>
                                            <th>Motivo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {importReport.skipped_existing_skus.map((sku, i) => (
                                            <tr key={i}>
                                                <td className="text-muted" style={{width: '40px'}}><small>{i + 1}</small></td>
                                                <td><code>{sku}</code></td>
                                                <td><small className="text-warning fw-bold">SKU già esistente</small></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Alert>
                        </div>
                    )}

                    {importReport.errors_detail && importReport.errors_detail.length > 0 && (
                        <div className="mt-3">
                            <h6 className="text-danger">
                                <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                Dettaglio Errori (primi 10)
                            </h6>
                            <Alert variant="danger" className="mb-0">
                                <ul className="mb-0">
                                    {importReport.errors_detail.map((error, idx) => (
                                        <li key={idx}><small>{error}</small></li>
                                    ))}
                                </ul>
                            </Alert>
                        </div>
                    )}

                    {hasSuccess && (
                        <Alert variant="success" className="mt-3 mb-0">
                            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                            {importReport.created > 0 && (
                                <><strong>{importReport.created}</strong> film {importReport.created === 1 ? 'è stato creato' : 'sono stati creati'}</>
                            )}
                            {importReport.created > 0 && importReport.updated > 0 && <> e </>}
                            {importReport.updated > 0 && (
                                <><strong>{importReport.updated}</strong> {importReport.updated === 1 ? 'è stato aggiornato' : 'sono stati aggiornati'}</>
                            )}
                            {' '}con successo!
                        </Alert>
                    )}

                    {allSkipped && (
                        <Alert variant="info" className="mt-3 mb-0">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            Nessun nuovo film è stato importato. Tutte le righe sono state saltate (probabilmente SKU già esistenti).
                        </Alert>
                    )}

                    {importReport.note && (
                        <Alert variant="warning" className="mt-3 mb-0">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                            <strong>Nota:</strong> {importReport.note}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleCloseReport}>
                        Chiudi
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    return (
        <Container>
            {renderReportModal()}
            
            <h2>Importa Film da CSV/Excel</h2>
            <Alert variant="info">
                <div className="mb-2">
                    <b>Campo obbligatorio:</b> Devi sempre mappare almeno uno tra <b>SKU</b>, <b>Barcode</b> o <b>Barcode IBS</b> per identificare i film.
                </div>
                <div>
                    <b>Per creare nuovi film:</b> Mappa anche il <b>Titolo</b> (obbligatorio).<br />
                    <b>Per aggiornare film esistenti:</b> Attiva la checkbox "Aggiorna film esistenti" e mappa solo i campi che vuoi modificare.
                </div>
            </Alert>
            {message && <Alert variant={messageType}>{message}</Alert>}
            
            {uploading && (
                <Alert variant="info">
                    <div className="mb-2">
                        <strong>Importazione in corso...</strong>
                    </div>
                    <ProgressBar 
                        now={uploadProgress} 
                        label={`${uploadProgress}%`}
                        animated 
                        striped 
                        variant="success"
                    />
                    <small className="text-muted mt-2 d-block">
                        Attendere il completamento dell'upload e dell'elaborazione dei dati.
                    </small>
                </Alert>
            )}
            
            <Form>
                <Form.Group controlId="formFile" className="mb-3">
                    <Form.Label>Seleziona un file CSV o Excel</Form.Label>
                    <Form.Control 
                        type="file" 
                        accept=".csv, .xls, .xlsx" 
                        onChange={handleFileChange}
                        disabled={uploading}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Check 
                        type="checkbox"
                        id="updateExistingCheck"
                        label="Aggiorna film esistenti (se SKU già presente, aggiorna solo i campi mappati)"
                        checked={updateExisting}
                        onChange={(e) => setUpdateExisting(e.target.checked)}
                        disabled={uploading}
                    />
                    {updateExisting && (
                        <Form.Text className="text-warning">
                            <small>
                                ⚠️ <strong>Attenzione:</strong> con questa opzione attiva, i film con SKU già esistente verranno aggiornati con i nuovi valori dei campi mappati.
                            </small>
                        </Form.Text>
                    )}
                </Form.Group>

                {csvColumns.length > 0 && !uploading && (
                    <div className="mb-3">
                        <h3>Mappa Colonne</h3>
                        <p>Associa le colonne del tuo file ai campi del database.</p>
                        {filmSchemaFields.map(field => {
                            const selectedCsvColumn = columnMapping[field.key];
                            const mappedColumns = Object.values(columnMapping).filter(val => val !== null);
                            const availableCsvColumns = csvColumns.filter(col => 
                                !mappedColumns.includes(col) || col === selectedCsvColumn
                            );

                            return (
                                <Row key={field.key} className="mb-2 align-items-center">
                                    <Col sm={4} className="text-sm-end">
                                        <Form.Label className="mb-0">{field.label} {field.required && <span className="text-danger">*</span>}{field.keyField && <span className="text-primary"> (chiave)</span>}</Form.Label>
                                    </Col>
                                    <Col sm={8}>
                                        <Form.Select
                                            value={selectedCsvColumn || ''}
                                            onChange={(e) => handleMappingChange(field.key, e.target.value)}
                                        >
                                            <option value="">Ignora</option>
                                            {availableCsvColumns.map(col => (
                                                <option key={col} value={col}>{col}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>
                            );
                        })}
                    </div>
                )}

                <Button 
                    variant="primary" 
                    onClick={handleUpload} 
                    disabled={!selectedFile || csvColumns.length === 0 || uploading}
                >
                    {uploading ? 'Importazione in corso...' : 'Importa'}
                </Button>
            </Form>
        </Container>
    );
};

export default FilmImport;
